import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { View, TouchableOpacity } from "react-native";
import { Mic } from "lucide-react";
import { SessionContext } from "../../../../contexts";
import type { ConsultPatient } from "./hooks/useConsultSearch";

// Components
import { ConsultHeader } from "./components/ConsultHeader";
import PatientListView from "./PatientListView";
import PatientPlanningView from "./PatientPlanningView";
import GalleryModal from "../../../common/native/GalleryModal";
import ConsultSettings from "./ConsultSettings";
import ConsultNotesView from "./ConsultNotesView";
import DateRangePicker from "./DateRangePicker";
import { StickyOnboardingModal } from "../../../common/native";
import { QuickRecordModal } from "./quick-record";

// Hooks
import { useConsultState } from "./hooks/useConsultState";
import { useDateRange } from "./hooks/useDateRange";
import { useConsultNavigation } from "./hooks/useConsultNavigation";
import { useConsultData } from "./hooks/useConsultData";
import { useConsultSearch } from "./hooks/useConsultSearch";
import { useDummyDataMode } from "./hooks/useDummyDataMode";
import { useConsultEffects } from "./hooks/useConsultEffects";
import { usePhotoGallery } from "./hooks/usePhotoGallery";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { useOnboardingSettings } from "../../../../contexts/OnboardingSettingsContext";

// Utils
import {
  getFilteredScheduleData,
  findTodaySectionIndex,
} from "../../../../services/consults-utils";
import { getPatients } from "../../../../services/api-clinic";
import {
  createImageEdit,
  getImageEditJob,
} from "../../../../services/api-clinic";
import { TClinicPatientType } from "../../../../utils/types";
import { trackEvent } from "../../../../utils/mixpanel";
import { createUUID } from "../../../../utils/functions";
import { consultSteps } from "../../../common/native/OnboardingSteps";
import { buildConsultPatientFromClinicPatient } from "./utils";
import styles from "./HomeConsults.styles";

type ConsultRouteState = {
  selectedPatient?: ConsultPatient | null;
};

const isSameConsultPatient = (
  patient: ConsultPatient | null | undefined,
  patientId: string,
) =>
  Boolean(
    patient &&
      [patient.id, patient.patientRecordId, patient.clinicOSUUID]
        .filter(Boolean)
        .some((id) => String(id) === patientId),
  );

const HomeConsults = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session ?? null;
  const clinicId = session?.clinicId;
  const routePatientId = searchParams.get("patientId");
  const routeState = location.state as ConsultRouteState | null;
  const handledRoutePatientIdRef = React.useRef<string | null>(null);

  const { tipsState, markStepComplete } = useOnboardingTips();
  const { tourActive, showConsultTips, setShowConsultTips } =
    useOnboardingSettings();
  const [localTipsToggle, setLocalTipsToggle] = React.useState(false);
  const [pendingTodayScroll, setPendingTodayScroll] = React.useState(false);
  const [quickRecordDate, setQuickRecordDate] = React.useState<string | null>(
    null,
  );
  const tipsEnabled = tourActive ? showConsultTips : localTipsToggle;

  // Custom hooks for state management
  const state = useConsultState();
  const dateRange = useDateRange(session);
  const dummyData = useDummyDataMode(session, state.setLoading);

  useEffect(() => {
    if (
      !session ||
      !routePatientId ||
      handledRoutePatientIdRef.current === routePatientId
    ) {
      return;
    }

    const routeSelectedPatient = routeState?.selectedPatient ?? null;

    if (isSameConsultPatient(routeSelectedPatient, routePatientId)) {
      state.setSelectedPatient(routeSelectedPatient);
      state.setCurrentView("planningView");
      handledRoutePatientIdRef.current = routePatientId;
      return;
    }

    let isMounted = true;

    const fetchRoutePatient = async () => {
      try {
        state.setLoading(true);
        const patients = await getPatients(session, {
          patientTypes: [
            TClinicPatientType.PATIENT,
            TClinicPatientType.CONSULT,
            TClinicPatientType.LEAD,
          ],
          patientIds: [routePatientId],
        });

        if (!isMounted) return;

        const patient = patients[0];
        if (patient) {
          state.setSelectedPatient(
            buildConsultPatientFromClinicPatient(patient),
          );
          state.setCurrentView("planningView");
        }
      } catch (error) {
        console.error("Error fetching patient for consult handoff:", error);
      } finally {
        if (isMounted) {
          state.setLoading(false);
          handledRoutePatientIdRef.current = routePatientId;
        }
      }
    };

    fetchRoutePatient();

    return () => {
      isMounted = false;
    };
  }, [
    routePatientId,
    routeState?.selectedPatient,
    session,
    state.setCurrentView,
    state.setLoading,
    state.setSelectedPatient,
  ]);

  // Data fetching
  const data = useConsultData({
    currentDateRange: dateRange.currentDateRange,
    clinicId,
    session,
    demoMode: false,
    useDummyData: dummyData.useDummyData,
    selectedPatient: state.selectedPatient,
    memoizedPatient: state.memoizedPatient,
  });

  const photoGallery = usePhotoGallery({
    selectedPatient: state.selectedPatient,
    setSelectedPhotos: state.setSelectedPhotos,
    setSelectedPhotoThumbnails: state.setSelectedPhotoThumbnails,
    setSelectedPatientName: state.setSelectedPatientName,
    setGalleryModalVisible: state.setGalleryModalVisible,
    setSelectedPhotoEditTargets: state.setSelectedPhotoEditTargets,
    setSelectedPhotoMetadata: state.setSelectedPhotoMetadata,
    setLoading: state.setLoading,
    session,
    currentView: state.currentView,
  });

  const handleSaveImageEdit = useCallback(
    async ({
      target,
      degrees,
    }: {
      target: { targetType: "clinic_patient_image"; targetId: number };
      degrees: number;
    }) => {
      if (!session) {
        throw new Error("Session unavailable. Sign in again and retry.");
      }

      const job = await createImageEdit(session, {
        target_type: target.targetType,
        target_id: target.targetId,
        operations: [{ type: "rotate", degrees }],
        client_request_id: createUUID(),
      });

      const startedAt = Date.now();
      while (Date.now() - startedAt < 60000) {
        const current = await getImageEditJob(session, job.task_id);
        if (current.status === "completed") {
          state.setPhotoEditRefreshKey((value) => value + 1);
          return;
        }
        if (current.status === "failed") {
          throw new Error(
            current.error_message || "Unable to save changes. Try again.",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      throw new Error("Image edit is still processing. Try again shortly.");
    },
    [session, state.setPhotoEditRefreshKey],
  );

  // Navigation
  const navigation = useConsultNavigation({
    currentView: state.currentView,
    setCurrentView: state.setCurrentView,
    selectedPatient: state.selectedPatient,
    setSelectedPatient: state.setSelectedPatient,
    setLoading: state.setLoading,
    session,
    markStepComplete,
    fetchStarredSimulations:
      photoGallery.fetchStarredSimulationsForPresentation,
    setSimulations: state.setSimulations,
  });

  // Search functionality
  const search = useConsultSearch({
    searchTerm: state.searchTerm,
    setSearchTerm: state.setSearchTerm,
    setIsSearching: state.setIsSearching,
    setSearchMode: state.setSearchMode,
    setSearchResults: state.setSearchResults,
    session,
    currentView: state.currentView,
    onPatientSelect: navigation.handlePatientSelect,
    tipsEnabled,
    tipsState,
  });

  // All effects consolidated
  useConsultEffects({
    state,
    dateRange,
    dummyData,
    data,
    navigation,
    session,
    tipsEnabled,
  });

  useEffect(() => {
    if (data.simulationRows) {
      state.setSimulations(data.simulationRows);
    }
  }, [data.simulationRows, state.setSimulations]);

  // Computed values
  const filteredData = useMemo(
    () =>
      getFilteredScheduleData(
        data.scheduleData,
        state.searchTerm,
        state.searchMode,
        state.searchResults,
        dateRange.viewMode,
        dateRange.viewMode === "daily"
          ? dateRange.formattedStartDate
          : undefined,
      ),
    [
      data.scheduleData,
      state.searchTerm,
      state.searchMode,
      state.searchResults,
      dateRange.viewMode,
      dateRange.formattedStartDate,
    ],
  );

  const scrollToToday = useCallback(() => {
    if (!state.scrollRef.current || !data.scheduleData?.length) {
      return;
    }

    const todaySectionIndex = findTodaySectionIndex(data.scheduleData);
    if (todaySectionIndex === -1) {
      return;
    }

    try {
      state.scrollRef.current.scrollToLocation({
        sectionIndex: todaySectionIndex,
        itemIndex: 0,
        animated: true,
        viewOffset: 0,
      });
    } catch (error) {
      console.error("Error scrolling to today section:", error);
    }
  }, [data.scheduleData, state.scrollRef]);

  const handleQuickRecordReady = useCallback(
    (patient: ConsultPatient) => {
      state.setSelectedPatient({
        ...patient,
        autoStartRecording: true,
      });
      state.setCurrentView("planningView");
    },
    [state.setCurrentView, state.setSelectedPatient],
  );

  const handleOpenQuickRecord = useCallback(
    (appointmentDate?: string | null) => {
      setQuickRecordDate(appointmentDate || null);
      state.setShowQuickRecordModal(true);
    },
    [state.setShowQuickRecordModal],
  );

  const handleCloseQuickRecord = useCallback(() => {
    state.setShowQuickRecordModal(false);
    setQuickRecordDate(null);
  }, [state.setShowQuickRecordModal]);

  const handleTodayPress = useCallback(() => {
    if (dateRange.viewMode === "weekly") {
      setPendingTodayScroll(true);
    }
    dateRange.handleResetRange();
  }, [dateRange]);

  const selectedTopLevelView = useMemo(() => {
    if (state.currentView === "consultSettings") {
      return "Settings";
    }

    if (state.currentView === "consultNotes") {
      return "Notes";
    }

    return "Appointments";
  }, [state.currentView]);

  const handleTopLevelViewChange = useCallback(
    (view: "Appointments" | "Notes" | "Settings") => {
      if (view === "Settings") {
        state.setSelectedPatient(null);
        state.setCurrentView("consultSettings");
        return;
      }

      if (view === "Notes") {
        state.setSelectedPatient(null);
        state.setCurrentView("consultNotes");
        return;
      }

      if (
        state.currentView === "consultSettings" ||
        state.currentView === "consultNotes"
      ) {
        state.setSelectedPatient(null);
        state.setCurrentView("baseView");
      }
    },
    [state.currentView, state.setCurrentView, state.setSelectedPatient],
  );

  useEffect(() => {
    if (!pendingTodayScroll) {
      return;
    }

    if (dateRange.viewMode !== "weekly") {
      setPendingTodayScroll(false);
      return;
    }

    const hasScheduleData = Array.isArray(data.scheduleData);
    if (data.isLoading || !hasScheduleData) {
      return;
    }

    const timer = setTimeout(() => {
      scrollToToday();
      setPendingTodayScroll(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [
    pendingTodayScroll,
    dateRange.viewMode,
    data.isLoading,
    data.scheduleData,
    scrollToToday,
  ]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <ConsultHeader
        currentView={state.currentView}
        selectedPatient={state.selectedPatient}
        useDummyData={dummyData.useDummyData}
        tipsEnabled={tipsEnabled}
        localTipsToggle={localTipsToggle}
        tourActive={tourActive}
        onBack={navigation.handleBack}
        onToggleDummyData={dummyData.toggleDummyData}
        onToggleTips={() => setLocalTipsToggle(!localTipsToggle)}
        selectedTopLevelView={selectedTopLevelView}
        onTopLevelViewChange={handleTopLevelViewChange}
        setShowConsultTips={setShowConsultTips}
      />

      {/* Main Content */}
      <View style={styles.contentContainer}>
        {state.currentView === "baseView" && (
          <PatientListView
            scheduleData={data.scheduleData}
            isLoading={data.isLoading}
            isError={data.isError}
            ehrConnected={state.ehrConnected}
            filteredScheduleData={filteredData}
            searchTerm={state.searchTerm}
            setSearchTerm={state.setSearchTerm}
            handleSearchTermChange={search.handleSearchTermChange}
            isSearching={state.isSearching}
            searchMode={state.searchMode}
            setSearchMode={state.setSearchMode}
            setSearchResults={state.setSearchResults}
            scrollRef={state.scrollRef}
            headerHeight={state.headerHeight}
            refreshSchedule={data.refreshSchedule}
            loadMoreDays={data.loadMoreDays}
            renderSectionHeader={search.renderSectionHeader}
            renderPatientItem={search.renderPatientItem}
            viewLabel={dateRange.viewLabel}
            toggleViewMode={dateRange.toggleViewMode}
            viewMode={dateRange.viewMode}
            session={session}
            onOpenRangePicker={() => state.setRangePickerVisible(true)}
            onShiftRange={dateRange.handleShiftRange}
            onTodayPress={handleTodayPress}
            rangeOffset={dateRange.rangeOffset}
            visibleSectionIndex={state.visibleSectionIndex}
            setVisibleSectionIndex={state.setVisibleSectionIndex}
            scrollY={state.scrollY}
            setScrollY={state.setScrollY}
            tipsEnabled={tipsEnabled}
            onOpenQuickRecord={handleOpenQuickRecord}
            useDummyData={dummyData.useDummyData}
            onUseDemoDataForTesting={dummyData.enableDummyDataForTesting}
          />
        )}

        {state.currentView === "planningView" && state.selectedPatient && (
          <PatientPlanningView
            selectedPatient={state.selectedPatient}
            voiceNoteRefreshKey={state.voiceNoteRefreshKey}
            setVoiceNoteRefreshKey={state.setVoiceNoteRefreshKey}
            setSelectedPhotos={state.setSelectedPhotos}
            setSelectedPhotoThumbnails={state.setSelectedPhotoThumbnails}
            setSelectedPatientName={state.setSelectedPatientName}
            setSelectedPhotoIndex={state.setSelectedPhotoIndex}
            setSelectedPhotoEditTargets={state.setSelectedPhotoEditTargets}
            setSelectedPhotoMetadata={state.setSelectedPhotoMetadata}
            setGalleryModalVisible={state.setGalleryModalVisible}
            session={session}
            currentView={state.currentView}
            setSelectedPatient={state.setSelectedPatient}
            tipsEnabled={tipsEnabled}
            photoEditRefreshKey={state.photoEditRefreshKey}
          />
        )}

        {state.currentView === "consultSettings" && <ConsultSettings />}
        {state.currentView === "consultNotes" && (
          <ConsultNotesView useDummyData={dummyData.useDummyData} />
        )}
      </View>

      {/* Modals */}
      <GalleryModal
        visible={state.galleryModalVisible}
        onClose={() => state.setGalleryModalVisible(false)}
        imageUrls={state.selectedPhotos}
        thumbnailUrls={state.selectedPhotoThumbnails}
        selectedIndex={state.selectedPhotoIndex}
        editableImages={state.selectedPhotoEditTargets}
        showImageMetadata
        imageMetadata={state.selectedPhotoMetadata}
        onSaveEdit={handleSaveImageEdit}
      />

      <DateRangePicker
        visible={state.rangePickerVisible}
        onClose={() => state.setRangePickerVisible(false)}
        options={dateRange.rangeOptions}
        onSelect={dateRange.handleRangeSelect}
        activeOffset={dateRange.rangeOffset}
        viewMode={dateRange.viewMode}
      />

      <QuickRecordModal
        visible={state.showQuickRecordModal}
        session={session}
        appointmentDate={quickRecordDate}
        onClose={handleCloseQuickRecord}
        onReady={handleQuickRecordReady}
      />

      {state.currentView === "baseView" && (
        <TouchableOpacity
          style={styles.quickRecordFab}
          onPress={() => handleOpenQuickRecord()}
        >
          <Mic size={22} color="#FFFFFF" />
        </TouchableOpacity>
      )}

      {tipsEnabled && <StickyOnboardingModal steps={consultSteps} />}
    </View>
  );
};

export default HomeConsults;
